import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
  EventType,
  EventTypeCategory
} from '../../../../../../../models/event-type';
import { EventTypeService } from '../../../../../../../services/event-type/event-type.service';
import { MyAccount } from '../../../../../../../models/my-account';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { AccountService } from '../../../../../../../services/account/account.service';
import { SimpleMessageModalComponent } from '../../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { getUrlHashDerivedFromText } from '../../../../../../../utils';
import { ComponentHasUnsavedChanges } from '../../../../../../../unsaved-changes-guard.service';
import { EventTypeStatus } from '../../../../../../../models/event-type-enums';


@Component({
  selector: 'app-event-type-category-detail',
  templateUrl: './event-type-category-detail.component.html',
  styleUrls: ['./event-type-category-detail.component.css']
})
export class EventTypeCategoryDetailComponent implements OnInit, ComponentHasUnsavedChanges {
  public isNewCategory: boolean;
  public category: EventTypeCategory = {
    id: null,
    url: null,
    name: '',
    url_hash: '',
    assigned_event_types: [],
    assigned_event_type_ids: [],
    created_at: null,
    updated_at: null
  };
  public eventTypes: EventType[];

  // Used to ensure retired event types are included as options if they are
  // already assigned to the category.
  // Otherwise, retired event types are not shown.
  public initialEventTypeIds: number[] = [];

  public errorMessage = '';
  public errors: any = {};

  public urlHashEdited = false;

  public loadingExistingCategory = false;
  public loadingEventTypes = false;
  public loadingSubmit = false;
  public loadingDelete = false;

  public categoryNotFound = false;
  public serverError: number = null;
  public unsavedChanges = false;

  public myAccount: MyAccount;

  public EventTypeStatus = EventTypeStatus;

  constructor(
    public orgConfig: OrganizationConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private eventTypeService: EventTypeService,
    private accountService: AccountService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewCategory = !routeParams.hasOwnProperty('id');

    this.myAccount = this.accountService.getMyAccount();
    if (!this.myAccount.is_organization_admin) { return; }

    // Get the category to edit, if an ID was provided.
    if (!this.isNewCategory) {
      const idInt = parseInt(routeParams.id, 10);
      if (isNaN(idInt)) {
        this.categoryNotFound = true;
        this.loadingExistingCategory = false;
        return;
      }

      this.loadCategory(idInt);

      this.urlHashEdited = true;
    }

    this.loadEventTypes();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  public loadCategory(id: number) {
    this.loadingExistingCategory = true;

    this.eventTypeService
      .getEventTypeCategory(id)
      .then(category => {
        this.category = category;
        this.category.assigned_event_type_ids = this.category.assigned_event_types.map(aet => aet.event_type_id);
        this.initialEventTypeIds = [...this.category.assigned_event_type_ids];
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.categoryNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingCategory = false; });
  }

  public loadEventTypes() {
    this.loadingEventTypes = true;

    this.eventTypeService
      .getEventTypes(1, 3000)
      .then(result => {
        this.eventTypes = result.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventTypes = false; });
  }

  public handleSubmit() {
    if (this.loadingSubmit) { return; }

    this.loadingSubmit = true;

    this.errorMessage = '';
    this.errors = {};

    let savePromise: Promise<EventTypeCategory>;
    if (this.isNewCategory) {
      savePromise = this.eventTypeService.addEventTypeCategory(this.category);
    } else {
      savePromise = this.eventTypeService.updateEventTypeCategory(this.category);
    }

    savePromise
      .then(savedCategory => {
        this.category = savedCategory;

        this.unsavedChanges = false;

        let successMessage: string;
        if (this.isNewCategory) {
          successMessage = this.category.name + ' created successfully.';
        } else {
          successMessage = this.category.name + ' updated successfully.';
        }

        this.router.navigate(
          ['/events/setup/categories'],
          {
            state: {
              feedback: {
                text: successMessage,
                status: 'success'
              }
            }
          }
        );
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        } else if (reason.status === 403) {
          if (this.isNewCategory) {
            this.errorMessage = 'You do not have permission to add event categories.';
          } else {
            this.errorMessage = 'You do not have permission to modify this category.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public handleDelete() {
    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Remove Category';
    modalRef.componentInstance.messageText =
      'Are you sure you want to remove this category? It will be hidden from ' +
      'staff and climbers, but any vouchers already issued in it stay valid, ' +
      'and you can restore it later.';
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingDelete = true;

      this.eventTypeService
        .deleteEventTypeCategory(this.category)
        .then(() => {
          this.unsavedChanges = false;

          this.router.navigate(
            ['/events/setup/categories'],
            {
              state: {
                feedback: {
                  text: this.category.name + ' removed.',
                  status: 'success'
                }
              }
            }
          )
        })
        .catch(reason => {
          if (reason.status === 400 && reason.error && reason.error.useful_error_message) {
            // The category can't be removed because an active or draft voucher
            // pack or membership still grants vouchers in it. Surface the
            // server's explanation naming what's blocking it, rather than a
            // generic form-validation message (PRO-1310).
            const errorModalRef = this.modalService.open(SimpleMessageModalComponent);
            errorModalRef.componentInstance.headerText = 'Cannot Remove Category';
            errorModalRef.componentInstance.messageText = reason.error.useful_error_message;
            errorModalRef.componentInstance.messageAlertClass = 'alert-danger';
          } else if (reason.status === 400) {
            this.errors = reason.error;
            this.errorMessage = 'Please correct the errors shown and try again.';
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to remove this category.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingDelete = false; });
    }
  }

  public getFilteredEventTypes(): EventType[] {
    return this.eventTypes.filter(et => {
      if ([EventTypeStatus.draft, EventTypeStatus.active].includes(et.status)) {
        return true;
      }

      // Allow retired event types if they are already assigned.
      if (this.initialEventTypeIds.includes(et.id)) { return true; }

      return false;
    });
  }

  public eventTypeSelected(eventType: EventType) {
    return this.category.assigned_event_type_ids.includes(eventType.id);
  }

  public toggleEventType(eventType: EventType) {
    if (this.eventTypeSelected(eventType)) {
      this.category.assigned_event_type_ids.splice(this.category.assigned_event_type_ids.indexOf(eventType.id), 1);
    } else {
      this.category.assigned_event_type_ids.push(eventType.id);
    }
  }

  public allEventTypesAreSelected() {
    for (const et of this.getFilteredEventTypes()) {
      if (!this.category.assigned_event_type_ids.includes(et.id)) {
        return false;
      }
    }

    return true;
  }

  public setNoEventTypesSelected() {
    this.category.assigned_event_type_ids = [];
  }

  public setAllEventTypesSelected() {
    this.category.assigned_event_type_ids = [];
    for (const et of this.getFilteredEventTypes()) {
      this.category.assigned_event_type_ids.push(et.id);
    }
  }

  public isLoading() {
    return (
      this.loadingExistingCategory ||
      this.loadingSubmit ||
      this.loadingDelete
    );
  }

  public handleNameChange() {
    if (!this.urlHashEdited) {
      this.category.url_hash = getUrlHashDerivedFromText(this.category.name);
    }
  }

  public handleUrlHashChange() { this.urlHashEdited = true; }
}
